import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { FormGroup, Label, Input, FormFeedback, InputGroup, InputGroupText } from "reactstrap";

interface DecimalInputProps {
  label?: string;
  name: string;
  value: string;
  setValue: (name: string, value: string) => void;
  setError?: (name: string, error: boolean) => void;
  placeholder?: string;
  validation?: (value: string) => boolean;
  errorMessage?: string;
  error?: boolean;
  required?: boolean;
  limit?: number;
  disabled?: boolean;
  passCol?: string;
  design?: "flex" | "default";
  tabIndex?: number;
  style?: any
}

const DecimalInput: FC<DecimalInputProps> = ({
  label,
  name,
  value,
  setValue,
  setError,
  placeholder = "",
  validation = () => true,
  errorMessage = "Invalid input",
  error = false,
  required = false,
  limit = 0,
  disabled,
  passCol,
  design = "default",
  tabIndex,
  style
}) => {
  const [formattedValue, setFormattedValue] = useState(value || "");
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatValue = (val: string = "") => {  
    let number = val.replace(/[^0-9.]/g, "");

    if (number.startsWith(".")) {
      number = "0" + number;
    }

    const [integer, decimal] = number.split('.');
    const totalLength = integer.length + (decimal ? decimal.length : 0);

    if (limit && totalLength > limit) {
      number = number.slice(0, limit);
    }

    if (!number.includes('.')) {
      if (number.length === 1 || number.length === 2) {
        number = `${number}.00`;
      } else if (number.length === 3) {
        number = `${number.slice(0, 1)}.${number.slice(1)}`;
      } else if (number.length >= 4) {
        number = `${number.slice(0, number.length - 2)}.${number.slice(number.length - 2)}`;
      }
    } else {
      if (decimal && decimal.length === 1) {
        if (totalLength < limit) {
          number = `${integer}.${decimal}0`;
        }
      } else if (decimal && decimal.length > 2) {
        number = `${integer}.${decimal.slice(0, 2)}`;
      }

      if (integer.length > 2) {
        const integerPart = integer.slice(0, integer.length - 2);
        const decimalPart = integer.slice(integer.length - 2) + (decimal || '');
        number = `${integerPart}.${decimalPart.slice(0, 2)}`;
      }
    }

    return number;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const [integer, decimal] = rawValue.split('.');

    const inputLength = (integer + (decimal || '')).length;

    if (limit && inputLength > limit) {
      return;
    }

    setFormattedValue(rawValue);
    setValue(name, rawValue);

    if (setError) {
      setError(name, false);
    }
  };

  const handleBlur = () => {
    if (!formattedValue) return;

    const newValue = formatValue(formattedValue);
    setFormattedValue(newValue);
    setValue(name, newValue);

    if (!validation(formattedValue) && setError) {
      setError(name, true);
    }
  };

  useEffect(() => {
    setFormattedValue(formatValue(value || ""));
  }, [value]);

  const flexClass = design === "flex" && windowWidth > 768 ? "d-flex align-items-center" : "";

  const labelWidthClass = design === "flex" && windowWidth > 768 ? "col-3" : "";
  const passColClass = windowWidth > 768 ? passCol : "";

  return (
    <FormGroup className={flexClass}>
      {label && (
        <Label for={name} className={`${design === "flex" ? "mr-5 " : ""}${labelWidthClass}`}>
          {label}
          {required && <span className="text-danger ml-2">*</span>}
        </Label>
      )}

      <div className={passColClass}>
        {error && <FormFeedback className="d-block mt-1">{errorMessage}</FormFeedback>}
        <InputGroup>
          <Input
            id={name}
            name={name}
            type="number"
            disabled={disabled}
            value={formattedValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            onWheel={(e : any) => e.target.blur()}
            tabIndex={tabIndex}
            style={style}
            invalid={error}
          />
          <InputGroupText>%</InputGroupText>
        </InputGroup>
      </div>
    </FormGroup>
  );
};

export default DecimalInput;
